import React from "react";
import {FilterItem, FilterItemProps, InputEditor, SelectEditor} from "../tablefilter/TableFilter";
import {SearchFilter} from "../tablefilter/SearchFilter";
import {FindingSeverity, isFindingSeverity} from "../../domain/FindingSeverity";
import {isString} from "../../utils/StringUtils";

export interface SbomComponentFilterValues {
    name: string;
    severity?: FindingSeverity;
    licenses: string[];
}

function SeverityItem(props: FilterItemProps) {
    return (
        <SelectEditor
            options={Object.keys(FindingSeverity).map(severity => ({
                value: severity,
                label: <span style={{display: "inline-block"}} className={"selection-item-text"}>&gt;= {severity}</span>
            }))}
            multiple={false}
            value={props.value}
            onChange={props.onChange}
        />
    )
}

interface LicensesProps extends FilterItemProps {
    licenses?: string[];
}

function LicensesItem(props: LicensesProps) {
    return (
        <SelectEditor
            options={props.licenses?.map(license => ({
                value: license,
                label: <span style={{display: "inline-block"}} className={"selection-item-text"}>{license}</span>
            }))}
            multiple={true}
            value={props.value}
            onChange={props.onChange}
        />
    )
}

function NameItem(props: FilterItemProps) {
    return (
        <InputEditor
            value={props.value}
            onChange={props.onChange}
        />
    )
}

interface FilterProps {
    className?: string;
    values?: SbomComponentFilterValues;
    onChange: (values: SbomComponentFilterValues) => void;
    immediateMode?: boolean;
    licenses?: string[];
    persistentStateIdent?: string;
}

export function createDefaultSbomComponentFilterValues(): SbomComponentFilterValues {
    return {severity: undefined, name: "", licenses: []};
}

export function normalizeSbomComponentFilterValues(values: unknown): SbomComponentFilterValues {
    const source = isObjectRecord(values) ? values : {};

    return {
        name: typeof source.name === "string" ? source.name : "",
        severity: normalizeSeverity(source.severity),
        licenses: Array.isArray(source.licenses) ? source.licenses.filter(isString) : []
    };
}

function SbomComponentFilter(filterProps: FilterProps) {
    const items: FilterItem[] = [
        {
            name: "name",
            key: "name",
            label: "Name",
            render: (props) => <NameItem {...props} />
        },
        {
            name: "severity",
            key: "severity",
            label: "Finding severity",
            render: (props) => <SeverityItem {...props} />
        },
        {
            name: "licenses",
            key: "licenses",
            label: "Licenses",
            render: (props) => <LicensesItem {...props} licenses={filterProps.licenses} />
        },
    ];

    return (
        <SearchFilter
            items={items}
            className={filterProps.className}
            values={filterProps.values}
            defaultValues={createDefaultSbomComponentFilterValues()}
            persistentStateIdent={filterProps.persistentStateIdent}
            defaultActiveFilterNames={["name", "severity", "licenses"]}
            immediateMode={filterProps.immediateMode}
            onChange={filterProps.onChange}
        />
    )
}

function normalizeSeverity(value: unknown): FindingSeverity | undefined {
    if (isFindingSeverity(value)) {
        return value;
    }

    if (!Array.isArray(value)) {
        return undefined;
    }

    return value.find(isFindingSeverity);
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

export default SbomComponentFilter;
